import { useAtom } from "jotai";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ActiveFilters from "./ActiveFilters";
import ColumnVisibilityDropdown from "./ColumnVisibilityDropdown";
import SpecialFiltersDropdown from "./SpecialFiltersDropdown";
import ExportButtons from "./ExportButtons";
import styles from "./FlotteTable.module.scss";
import FlotteTableHeader from "./FlotteTableHeader";
import FlotteTableRow from "./FlotteTableRow";
import SummaryCard from "./SummaryCard";
import useFlotteTable, { visibleHeadersAtom } from "./useFlotteTable";
import Loader from "../../../../common/Loader";
import { getAnomaliesFacturation, isVORole } from "./FlotteUtils";
import { connectedUserAtom } from "../../../../../middlewares/AuthMiddleware";

const FlotteTable = () => {
  const [visibleHeaders] = useAtom(visibleHeadersAtom);
  const [connectedUser] = useAtom(connectedUserAtom);
  const {
    flotteList,
    flotteMeta,
    isLoading,
    tableContainerRef,
    isDragging,
    hoveredRowIndex,
    showSummaryCard,
    hoveredRowTop,
    nbCells,
    stickyIdentite,
    setStickyIdentite,
    isScrolledX,
    setVisibleHeaders,
    handleRowMouseEnter,
    lastRowRefCallback,
    setHoveredRowIndex,
    // Fonctions de sélection
    toggleRowSelection,
    toggleAllRowsSelection,
    isRowSelected,
    isAllRowsSelected,
    isSomeRowsSelected,
    // Actions en masse
    selectedRows,
    simulerEnMasse,
    modifierSecteurEnMasse,
  } = useFlotteTable();

  const [activeDetailId, setActiveDetailId] = useState(null);
  const [modalOpenId, setModalOpenId] = useState(null);
  const handleOpenDetail = useCallback((itemId) => {
    setActiveDetailId(itemId);
    setModalOpenId(itemId);
  }, []);
  const handleCloseModal = useCallback(() => {
    setModalOpenId(null);
  }, []);

  // Fermer la modale avec Échap
  useEffect(() => {
    if (!modalOpenId) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") handleCloseModal();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [modalOpenId, handleCloseModal]);

  // Mémoriser les lignes du tableau pour éviter les rendus inutiles
  const tableRows = useMemo(() => {
    return flotteList.map((item, index) => {
      const isLastRow = index === flotteList.length - 1;

      return (
        <FlotteTableRow
          key={item.id}
          item={item}
          rowRef={isLastRow ? lastRowRefCallback : null}
          visibleHeaders={visibleHeaders}
          onMouseEnter={(event) => handleRowMouseEnter(index, event)}
          onMouseLeave={() => setHoveredRowIndex(-1)}
          isRowSelected={isRowSelected}
          toggleRowSelection={toggleRowSelection}
          isDetailActive={activeDetailId === item.id}
          isModalOpen={modalOpenId === item.id}
          onOpenDetail={handleOpenDetail}
          onCloseModal={handleCloseModal}
          stickyIdentite={stickyIdentite}
        />
      );
    });
  }, [
    flotteList,
    lastRowRefCallback,
    visibleHeaders,
    handleRowMouseEnter,
    setHoveredRowIndex,
    isRowSelected,
    toggleRowSelection,
    activeDetailId,
    modalOpenId,
    handleOpenDetail,
    handleCloseModal,
    stickyIdentite,
  ]);

  return (
    <>
      <div
        className={`${styles.tableControls} d-flex justify-content-between align-items-center mb-0`}
      >
        <div className={styles.resultsCount}>
          {isLoading && (
            <div className="position-absolute" style={{ left: "50%" }}>
              <Loader size={18} />
            </div>
          )}
          {flotteMeta?.total && (
            <span className="text-muted">
              <i className="fas fa-car mr-2"></i>
              {flotteMeta.total.toLocaleString()} véhicule
              {flotteMeta.total > 1 ? "s" : ""} au total
              {flotteList.length > 0 &&
                flotteList.length < flotteMeta.total && (
                  <span className="ml-2">
                    ({flotteList.length.toLocaleString()} affiché
                    {flotteList.length > 1 ? "s" : ""})
                  </span>
                )}
              {/* Dropdown d'actions en masse */}
              {selectedRows.size > 0 && !isVORole(connectedUser?.role) && (
                <>
                  <button
                    className="btn btn-primary btn-sm ml-2"
                    onClick={simulerEnMasse}
                  >
                    <i className="fas fa-calculator mr-2 text-white"></i>
                    Simulation en masse ({selectedRows.size})
                  </button>
                  <button
                    className="btn btn-primary btn-sm ml-2"
                    onClick={modifierSecteurEnMasse}
                  >
                    <i className="fas fa-pen mr-2 text-white"></i>
                    Modifier le secteur en masse ({selectedRows.size})
                  </button>
                </>
              )}
            </span>
          )}
        </div>

        <div className="d-flex align-items-center" style={{ gap: 10 }}>
          <SpecialFiltersDropdown />
          <ActiveFilters />
          <label
            className="mb-0 d-flex align-items-center text-muted"
            style={{ gap: 5, cursor: "pointer", whiteSpace: "nowrap" }}
            title="Garde une colonne immatriculation / modèle / client / durée / km toujours visible pendant le défilement horizontal (remplace la bulle au survol)"
          >
            <input
              type="checkbox"
              checked={stickyIdentite}
              onChange={(e) => setStickyIdentite(e.target.checked)}
            />
            Figer les infos véhicule
          </label>
          <ExportButtons flotteList={flotteList} flotteMeta={flotteMeta} />
          <ColumnVisibilityDropdown
            visibleHeaders={visibleHeaders}
            setVisibleHeaders={setVisibleHeaders}
            className="ml-2"
          />
        </div>
      </div>
      <div
        ref={tableContainerRef}
        className={`${styles.tableContainer} table-responsive ${
          isDragging ? styles.dragging : ""
        } ${stickyIdentite ? styles.stickyActive : ""} ${
          stickyIdentite && isScrolledX ? styles.scrolledX : ""
        }`}
        style={{ position: "relative" }}
      >
        {/* Aide visuelle au survol, remplacée par la colonne figée (LS-707)
            quand celle-ci est active */}
        {!stickyIdentite &&
          showSummaryCard &&
          hoveredRowIndex >= 0 &&
          flotteList[hoveredRowIndex] && (
            <SummaryCard
              marque={flotteList[hoveredRowIndex].identification.marque}
              modele={flotteList[hoveredRowIndex].identification.modele}
              immatriculation={
                flotteList[hoveredRowIndex].identification.immatriculation
              }
              client={flotteList[hoveredRowIndex].contrat.intitule_client}
              duree_contrat={flotteList[hoveredRowIndex].contrat.duree_mois}
              km_contrat={flotteList[hoveredRowIndex].contrat.kilometrage}
              anomalies_facturation={getAnomaliesFacturation(
                flotteList[hoveredRowIndex],
              )}
              top={hoveredRowTop}
            />
          )}

        <table
          className="table flotteTable"
          style={{
            opacity: isLoading ? "0.3" : "1",
            transition: "opacity 200ms ease-in-out",
          }}
        >
          <thead className="table-light">
            <FlotteTableHeader
              visibleHeaders={visibleHeaders}
              setVisibleHeaders={setVisibleHeaders}
              isAllRowsSelected={isAllRowsSelected}
              isSomeRowsSelected={isSomeRowsSelected}
              toggleAllRowsSelection={toggleAllRowsSelection}
              stickyIdentite={stickyIdentite}
            />
          </thead>
          <tbody>
            {tableRows}
            {isLoading && (
              <tr>
                <td colSpan={nbCells} className="text-center py-3">
                  <div
                    className="spinner-border text-primary"
                    role="status"
                  ></div>
                </td>
              </tr>
            )}
            {!isLoading && flotteList.length === 0 && (
              <tr>
                <td colSpan={nbCells} className="text-center py-4 text-muted">
                  Aucun véhicule trouvé
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default FlotteTable;
